import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import cookie from "react-cookies";
import {
  extractCertificate,
  getAcademyCertificates,
  getCertificationEligibility,
} from "../../services/academyApi";
import { QtBackLink } from "../../components/icons/QtIcon";
import {
  TrainingHero,
  TrainingLoading,
  TrainingFeedback,
  CertBlockersList,
  CertRequiredModulesList,
  CertRequirementSummary,
  CertStatusBadge,
  CertDownloadButton,
  certDetailPath,
} from "../academy/AcademyShared";
import { displayProgramTitle } from "../academy/trainingTerms";
import MeLayout from "./MeLayout";

function formatCompetenceLevel(level) {
  if (!level) return null;
  return `${level.charAt(0).toUpperCase()}${level.slice(1)} level`;
}

export default function MeCertificateDetail() {
  const { programSlug, certSlug } = useParams();
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ladderRes, issuedRes] = await Promise.all([
        getCertificationEligibility(),
        getAcademyCertificates(),
      ]);
      const found = (ladderRes.certifications || []).find(
        (c) => c.slug === certSlug && c.academy_slug === programSlug
      );
      if (!found) {
        setCert(null);
        return;
      }
      const issuedRow = (issuedRes.certificates || []).find(
        (c) =>
          c.status === "issued" &&
          (c.certification_slug === certSlug || c.slug === certSlug) &&
          c.academy_slug === programSlug
      );
      setCert({
        ...found,
        ...(issuedRow && {
          certificate_id: issuedRow.id,
          certificate_number: issuedRow.certificate_number,
          issued_at: issuedRow.issued_at,
          cert_status: "issued",
        }),
      });
    } catch {
      setCert(null);
      setFeedback({ type: "error", message: "Could not load this certificate." });
    } finally {
      setLoading(false);
    }
  }, [certSlug, programSlug]);

  useEffect(() => {
    if (!cookie.load("candidateKey")) {
      window.location.href = `/login?next=${encodeURIComponent(certDetailPath(programSlug, certSlug))}`;
      return;
    }
    load();
  }, [load, programSlug, certSlug]);

  const handleDownload = async () => {
    if (!cert?.certificate_id) return;
    setExtracting(true);
    setFeedback(null);
    try {
      const res = await extractCertificate(cert.certificate_id);
      if (res.download_url) {
        window.open(res.download_url, "_blank", "noopener,noreferrer");
        setFeedback({ type: "success", message: "Certificate download started." });
      }
    } catch (e) {
      setFeedback({ type: "error", message: e.message || "Download failed." });
    } finally {
      setExtracting(false);
    }
  };

  const copyVerifyLink = () => {
    if (!cert?.certificate_number) return;
    const url = `${window.location.origin}/verify/${cert.certificate_number}`;
    navigator.clipboard?.writeText(url);
    setFeedback({ type: "success", message: "Verify link copied." });
  };

  if (loading) {
    return (
      <MeLayout title="Certificate">
        <TrainingLoading label="Loading certificate..." />
      </MeLayout>
    );
  }

  if (!cert) {
    return (
      <MeLayout title="Certificate">
        <TrainingHero title="Certificate not found" subtitle="This tier may not exist for that program." />
        <QtBackLink to="/me/certificates" className="training-btn-secondary" style={{ marginTop: 20 }}>
          Back to certificates
        </QtBackLink>
      </MeLayout>
    );
  }

  const programTitle = displayProgramTitle(cert.academy_title || programSlug);

  return (
    <MeLayout title={cert.title}>
      <TrainingHero
        eyebrow={programTitle}
        title={cert.title}
        subtitle={
          cert.description ||
          `Earn this certificate by completing every required module in the ${programTitle}.`
        }
      />

      <TrainingFeedback feedback={feedback} />

      <div className="training-cert-detail">
        <div className="training-cert-detail__head">
          <CertStatusBadge status={cert.cert_status || "locked"} />
          {cert.competence_level && (
            <span className="training-badge-level">{formatCompetenceLevel(cert.competence_level)}</span>
          )}
        </div>

        {cert.cert_status === "issued" && cert.issued_at && (
          <p className="training-cert-detail__issued">
            Issued {new Date(cert.issued_at).toLocaleDateString("en-ZA", { dateStyle: "long" })}
            {cert.certificate_number ? ` · ${cert.certificate_number}` : ""}
          </p>
        )}

        <section className="training-cert-detail__section">
          <h3 className="training-section-title">What this certificate is for</h3>
          <p className="training-cert-detail__about">
            {cert.description ||
              "This tier confirms you completed every required module and met the quiz standards for this program."}
          </p>
          <CertRequirementSummary cert={cert} />
        </section>

        <section className="training-cert-detail__section">
          <h3 className="training-section-title">Required modules</h3>
          <p className="training-cert-detail__hint">
            Finish every lesson and score 100% on each module quiz listed below.
          </p>
          <CertRequiredModulesList modules={cert.required_modules} showProgress />
        </section>

        {cert.cert_status === "in_progress" && cert.cert_progress?.blockers?.length > 0 && (
          <CertBlockersList
            items={[{ cert_slug: cert.slug, cert_title: cert.title, blockers: cert.cert_progress.blockers }]}
            title="Still needed for this certificate"
          />
        )}

        {cert.cert_status === "issued" && cert.certificate_id && (
          <div className="training-cert-card__actions" style={{ marginTop: 20 }}>
            <CertDownloadButton loading={extracting} onClick={handleDownload} />
            <button type="button" className="training-btn-secondary" onClick={copyVerifyLink}>
              Copy verify link
            </button>
          </div>
        )}

        {cert.cert_status !== "issued" && cert.required_modules?.length > 0 && (
          <Link
            to={cert.required_modules[0].href}
            className="training-btn-primary"
            style={{ display: "inline-flex", marginTop: 20, textDecoration: "none" }}
          >
            Continue learning
          </Link>
        )}
      </div>

      <QtBackLink to="/me/certificates" className="training-btn-secondary" style={{ marginTop: 24 }}>
        Back to certificates
      </QtBackLink>
    </MeLayout>
  );
}
